import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ButtonType, HeroButton, Icon, IconSize } from "@gaming/xds-react-js";
import { Filter } from "@gaming/xds-react-js-icons";

import { SelectDropdown } from "../../components/SelectDropdown";
import {
  TicketCategory,
  TicketSubCategory,
  TicketTeamData,
  TranslationMap,
} from "../../models/ticketing";
import * as styles from "./TicketingPage.module.css";
import {
  translateSeverityLabel,
  translateValue,
  TranslationEntity,
  TranslationField,
} from "./translateValue";

export type SortOption =
  | "newest"
  | "oldest"
  | "titleAsc"
  | "titleDesc"
  | "ticketNumDesc"
  | "ticketNumAsc";

export interface FilterPanelValues {
  sortOrder: SortOption;
  dateFrom: string;
  dateTo: string;
  createdBy: string;
  tags: string;
  severity: string;
  team: string;
  category: string;
  subCategory: string;
}

interface TicketingFilterPanelProps {
  severityLevels: string[];
  teams: TicketTeamData[];
  categories: TicketCategory[];
  subCategories: TicketSubCategory[];
  translationMap: TranslationMap;
  currentSeverityFilter: string;
  currentTeamFilter: string;
  strings: Record<string, string>;
  onApply: (values: FilterPanelValues) => void;
}

const defaultValues: FilterPanelValues = {
  sortOrder: "newest",
  dateFrom: "",
  dateTo: "",
  createdBy: "",
  tags: "",
  severity: "",
  team: "",
  category: "",
  subCategory: "",
};

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export const TicketingFilterPanel: React.FC<TicketingFilterPanelProps> = ({
  severityLevels,
  teams,
  categories,
  subCategories,
  translationMap,
  currentSeverityFilter,
  currentTeamFilter,
  strings,
  onApply,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [values, setValues] = useState<FilterPanelValues>(defaultValues);

  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const filteredSubCategories = useMemo(() => {
    if (!values.category) {
      return subCategories;
    }
    return subCategories.filter(sc => sc.categoryId === values.category);
  }, [subCategories, values.category]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (values.sortOrder !== "newest") {
      count++;
    }
    if (values.dateFrom) {
      count++;
    }
    if (values.dateTo) {
      count++;
    }
    if (values.createdBy) {
      count++;
    }
    if (values.tags) {
      count++;
    }
    if (values.severity) {
      count++;
    }
    if (values.team) {
      count++;
    }
    if (values.category) {
      count++;
    }
    if (values.subCategory) {
      count++;
    }
    return count;
  }, [values]);

  const handleOpen = useCallback(() => {
    setValues(prev => ({
      ...prev,
      severity: currentSeverityFilter,
      team: currentTeamFilter,
    }));
    setIsOpen(true);
  }, [currentSeverityFilter, currentTeamFilter]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Return focus to the trigger button
    setTimeout(() => {
      triggerButtonRef.current?.focus();
    }, 0);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Auto-focus close button when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  // Focus trap and Escape key handling
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
        return;
      }

      if (e.key === "Tab") {
        const panel = panelRef.current;
        if (!panel) {
          return;
        }
        const focusableElements = Array.from(
          panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        ).filter(el => !el.hasAttribute("disabled") && el.offsetParent !== null);

        if (focusableElements.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleClose]);

  const handleApply = useCallback(() => {
    onApply(values);
    setIsOpen(false);
    setTimeout(() => {
      triggerButtonRef.current?.focus();
    }, 0);
  }, [onApply, values]);

  const handleReset = useCallback(() => {
    setValues(defaultValues);
  }, []);

  const updateField = useCallback(
    <K extends keyof FilterPanelValues>(field: K, value: FilterPanelValues[K]) => {
      setValues(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  return (
    <>
      <button
        ref={triggerButtonRef}
        className={styles.filterButton}
        onClick={handleOpen}
        aria-label={strings.ticketFilterButtonAriaLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        type="button"
      >
        <Icon icon={Filter} iconSize={IconSize.XSmall} />
        {activeFilterCount > 0 && (
          <span className={styles.filterButtonBadge} aria-hidden="true">
            {activeFilterCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className={styles.filterOverlay} onClick={handleClose} aria-hidden="true" />
          <div
            ref={panelRef}
            className={styles.filterPanel}
            role="dialog"
            aria-modal="true"
            aria-labelledby="filter-panel-title"
          >
            <div className={styles.filterPanelHeader}>
              <h2 id="filter-panel-title" className={styles.filterPanelTitle}>
                {strings.ticketFilterByLabel}
              </h2>
              <button
                ref={closeButtonRef}
                className={styles.filterPanelClose}
                onClick={handleClose}
                aria-label={strings.ticketFilterClosePanelAriaLabel}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className={styles.filterPanelSection}>
              <label htmlFor="filter-sort-order" className={styles.filterPanelLabel}>
                {strings.ticketFilterSortOrderLabel}
              </label>
              <SelectDropdown
                className={styles.filterPanelSelect}
                value={values.sortOrder}
                onChange={v => updateField("sortOrder", v as SortOption)}
                options={[
                  { id: "newest", label: strings.ticketFilterNewestFirst },
                  { id: "oldest", label: strings.ticketFilterOldestFirst },
                  { id: "titleAsc", label: strings.ticketFilterTitleAsc },
                  { id: "titleDesc", label: strings.ticketFilterTitleDesc },
                  { id: "ticketNumDesc", label: strings.ticketFilterTicketNumberDesc },
                  { id: "ticketNumAsc", label: strings.ticketFilterTicketNumberAsc },
                ]}
                aria-label={strings.ticketFilterSortOrderLabel}
              />
            </div>

            <div className={styles.filterPanelSection}>
              <span id="filter-date-range-label" className={styles.filterPanelLabel}>
                {strings.ticketFilterDateRangeLabel}
              </span>
              <div
                className={styles.filterPanelDateRow}
                role="group"
                aria-labelledby="filter-date-range-label"
              >
                <input
                  type="date"
                  className={styles.filterPanelInput}
                  value={values.dateFrom}
                  onChange={e => updateField("dateFrom", e.target.value)}
                  aria-label={strings.ticketFilterDateFromAriaLabel}
                />
                <input
                  type="date"
                  className={styles.filterPanelInput}
                  value={values.dateTo}
                  onChange={e => updateField("dateTo", e.target.value)}
                  aria-label={strings.ticketFilterDateToAriaLabel}
                />
              </div>
            </div>

            <div className={styles.filterPanelSection}>
              <label htmlFor="filter-created-by" className={styles.filterPanelLabel}>
                {strings.ticketFilterCreatedByLabel}
              </label>
              <input
                id="filter-created-by"
                type="text"
                className={styles.filterPanelInput}
                placeholder={strings.ticketFilterCreatedByPlaceholder}
                value={values.createdBy}
                onChange={e => updateField("createdBy", e.target.value)}
              />
            </div>

            <div className={styles.filterPanelSection}>
              <label htmlFor="filter-tags" className={styles.filterPanelLabel}>
                {strings.ticketFilterTagsLabel}
              </label>
              <input
                id="filter-tags"
                type="text"
                className={styles.filterPanelInput}
                placeholder={strings.ticketFilterTagsPlaceholder}
                value={values.tags}
                onChange={e => updateField("tags", e.target.value)}
              />
            </div>

            <div className={styles.filterPanelSection}>
              <label htmlFor="filter-severity" className={styles.filterPanelLabel}>
                {strings.ticketFilterSeverityLabel}
              </label>
              <SelectDropdown
                className={styles.filterPanelSelect}
                value={values.severity}
                onChange={v => updateField("severity", v)}
                options={[
                  { id: "", label: strings.ticketAllSeveritiesLabel },
                  ...severityLevels.map(s => ({
                    id: s,
                    label: translateSeverityLabel(translationMap, s),
                  })),
                ]}
                aria-label={strings.ticketFilterSeverityLabel}
              />
            </div>

            <div className={styles.filterPanelSection}>
              <label htmlFor="filter-team" className={styles.filterPanelLabel}>
                {strings.ticketFilterTeamLabel}
              </label>
              <SelectDropdown
                className={styles.filterPanelSelect}
                value={values.team}
                onChange={v => updateField("team", v)}
                options={[
                  { id: "", label: strings.ticketAllTeamsLabel },
                  { id: "private", label: strings.ticketTeamPrivateOption },
                  ...teams.map(t => ({ id: t.id, label: t.name })),
                ]}
                aria-label={strings.ticketFilterTeamLabel}
              />
            </div>

            <div className={styles.filterPanelSection}>
              <label htmlFor="filter-category" className={styles.filterPanelLabel}>
                {strings.ticketFilterCategoryLabel}
              </label>
              <SelectDropdown
                className={styles.filterPanelSelect}
                value={values.category}
                onChange={v => {
                  updateField("category", v);
                  updateField("subCategory", "");
                }}
                options={[
                  { id: "", label: strings.ticketFilterAllCategoriesOption },
                  ...categories.map(c => ({
                    id: c.id,
                    label: translateValue(
                      translationMap,
                      TranslationEntity.product,
                      TranslationField.name,
                      c.id,
                      c.name
                    ),
                  })),
                ]}
                aria-label={strings.ticketFilterCategoryLabel}
              />
            </div>

            <div className={styles.filterPanelSection}>
              <label htmlFor="filter-sub-category" className={styles.filterPanelLabel}>
                {strings.ticketFilterSubCategoryLabel}
              </label>
              <SelectDropdown
                className={styles.filterPanelSelect}
                value={values.subCategory}
                onChange={v => updateField("subCategory", v)}
                options={[
                  { id: "", label: strings.ticketFilterAllSubCategoriesOption },
                  ...filteredSubCategories.map(sc => ({
                    id: sc.id,
                    label: translateValue(
                      translationMap,
                      TranslationEntity.productFeature,
                      TranslationField.name,
                      sc.id,
                      sc.name
                    ),
                  })),
                ]}
                aria-label={strings.ticketFilterSubCategoryLabel}
              />
            </div>

            <div className={styles.filterPanelFooter}>
              <HeroButton as="button" className={styles.filterPanelApply} onClick={handleApply}>
                {strings.ticketFilterApplyLabel}
              </HeroButton>
              <HeroButton
                as="button"
                buttonType={ButtonType.Secondary}
                className={styles.filterPanelReset}
                onClick={handleReset}
              >
                {strings.ticketFilterResetLabel}
              </HeroButton>
            </div>
          </div>
        </>
      )}
    </>
  );
};
